import { StatsigOptions } from '@statsig/react-bindings'
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics'

import { STATSIG_ENV } from '@src/config/env'

export function getStatsigOptions(): StatsigOptions {
  const options: StatsigOptions = {
    plugins: [new StatsigAutoCapturePlugin()],
    environment: {
      tier: STATSIG_ENV,
    },
    disableStorage: true, // Disable statsig caching to avoid hydration errors due to caching on client side.
    disableStableID: true,
  }
  return options
}

/**
 * Checks if a value is a plain object (not null, not array)
 */
function isMergeableObject(item: unknown): item is Record<string, unknown> {
  return typeof item === 'object' && item !== null && !Array.isArray(item)
}

/**
 * Low-level merge helper.
 *
 * Merge `defaultConfig` into `statsigConfig`: Statsig is the base, default only adds what Statsig is missing.
 * This is a deep merge (recursive).
 * - If the key is present in Statsig and is not an object, leave it as-is.
 * - If the key is present in Statsig and is an object, deep merge it with the default.
 * - If the key is not present in Statsig, add it from default.
 */
export function mergeStatsigLocaleConfig(
  defaultConfig: Record<string, unknown>,
  statsigConfig: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...statsigConfig }

  for (const key of Object.keys(defaultConfig)) {
    const defaultValue = defaultConfig[key]
    const statsigValue = statsigConfig[key]

    if (key in statsigConfig) {
      // If both are mergeable objects, recurse
      if (isMergeableObject(defaultValue) && isMergeableObject(statsigValue)) {
        result[key] = mergeStatsigLocaleConfig(defaultValue, statsigValue)
      }
      // Otherwise, keep the Statsig value (already in result)
      continue
    }

    // Key not in Statsig at all, take it from default
    result[key] = defaultValue
  }

  return result
}

/**
 * Preferred API to avoid parameter-order confusion.
 *
 * Statsig remains the base and wins on conflicts; defaults only fill missing keys.
 */
export function mergeDefaultIntoStatsigLocaleConfig(
  defaultConfig: Record<string, unknown>,
  statsigConfig: Record<string, unknown>,
): Record<string, unknown> {
  return mergeStatsigLocaleConfig(defaultConfig, statsigConfig)
}
